import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';

export type CurrencyType = 'BRL' | 'USD';
export type ThemeMode = 'dark' | 'midnight' | 'slate';
export type CardDisplayDensity = 'compact' | 'comfortable' | 'spacious';
export type LanguagePreference = 'pt-BR' | 'en';

export interface SiteSettings {
  currency: CurrencyType;
  usdToBrlRate: number; // e.g. 5.0 (can be updated or defaulted)
  cardDensity: CardDisplayDensity;
  showCardPrices: boolean;
  compactSidebar: boolean;
  enableSounds: boolean;
  reduceAnimations: boolean;
  language: LanguagePreference;
}

const DEFAULT_SETTINGS: SiteSettings = {
  currency: 'BRL',
  usdToBrlRate: 5.0,
  cardDensity: 'comfortable',
  showCardPrices: true,
  compactSidebar: false,
  enableSounds: true,
  reduceAnimations: false,
  language: 'pt-BR'
};

const STORAGE_KEY = 'cardumy_global_settings';

interface SettingsContextType {
  settings: SiteSettings;
  updateSetting: <K extends keyof SiteSettings>(key: K, value: SiteSettings[K]) => void;
  updateSettings: (newSettings: Partial<SiteSettings>) => void;
  resetSettings: () => void;
  formatPrice: (amountInBrl: number | string | null | undefined, options?: { showZero?: boolean; prefix?: string }) => string;
  convertPrice: (amountInBrl: number | string | null | undefined) => number;
  currencySymbol: string;
}

const SettingsContext = createContext<SettingsContextType | undefined>(undefined);

export const SettingsProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [settings, setSettings] = useState<SiteSettings>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        return { ...DEFAULT_SETTINGS, ...parsed };
      }
    } catch (e) {
      console.warn('Failed to load settings from localStorage:', e);
    }
    return DEFAULT_SETTINGS;
  });

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
    } catch (e) {
      console.warn('Failed to save settings to localStorage:', e);
    }
  }, [settings]);

  const updateSetting = useCallback(<K extends keyof SiteSettings>(key: K, value: SiteSettings[K]) => {
    setSettings((prev) => ({
      ...prev,
      [key]: value
    }));
  }, []);

  const updateSettings = useCallback((newSettings: Partial<SiteSettings>) => {
    setSettings((prev) => ({
      ...prev,
      ...newSettings
    }));
  }, []);

  const resetSettings = useCallback(() => {
    setSettings(DEFAULT_SETTINGS);
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (e) {
      console.warn('Failed to reset settings:', e);
    }
  }, []);

  const currencySymbol = settings.currency === 'USD' ? '$' : 'R$';

  const convertPrice = useCallback(
    (amountInBrl: number | string | null | undefined): number => {
      const num = Number(amountInBrl);
      if (isNaN(num) || num <= 0) return 0;
      if (settings.currency === 'USD') {
        const rate = settings.usdToBrlRate > 0 ? settings.usdToBrlRate : 5.0;
        return Math.round((num / rate) * 100) / 100;
      }
      return Math.round(num * 100) / 100;
    },
    [settings.currency, settings.usdToBrlRate]
  );

  const formatPrice = useCallback(
    (
      amountInBrl: number | string | null | undefined,
      options: { showZero?: boolean; prefix?: string } = {}
    ): string => {
      const { showZero = true, prefix = '' } = options;
      const num = Number(amountInBrl);

      if (isNaN(num) || (num <= 0 && !showZero)) {
        return '-';
      }

      if (settings.currency === 'USD') {
        const rate = settings.usdToBrlRate > 0 ? settings.usdToBrlRate : 5.0;
        const converted = Math.round((num / rate) * 100) / 100;
        return `${prefix}$ ${converted.toFixed(2)}`;
      }

      // Default BRL
      return `${prefix}R$ ${num.toFixed(2).replace('.', ',')}`;
    },
    [settings.currency, settings.usdToBrlRate]
  );

  return (
    <SettingsContext.Provider
      value={{
        settings,
        updateSetting,
        updateSettings,
        resetSettings,
        formatPrice,
        convertPrice,
        currencySymbol
      }}
    >
      {children}
    </SettingsContext.Provider>
  );
};

export const useSettings = (): SettingsContextType => {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
};
